import { useEffect, useRef, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { Headphones, Loader2, Play, Pause, RefreshCw, SkipBack, Check, Settings2 } from "lucide-react";

type Provider = "openai" | "elevenlabs";
type Format = "deep_dive" | "brief" | "critique" | "debate";
type Length = "short" | "default" | "long";
type DialogueLine = { host: "alex" | "jordan"; text: string };

interface PodcastRecord {
  id: string;
  storage_path: string;
  transcript: DialogueLine[];
  provider: Provider;
  created_at: string;
}

interface NodePodcastProps {
  nodeId: string;
  courseId: string;
}

const FORMATS: { id: Format; label: string; description: string }[] = [
  {
    id: "deep_dive",
    label: "Deep Dive",
    description: "A lively conversation between two hosts, unpacking and connecting topics in depth",
  },
  {
    id: "brief",
    label: "Brief",
    description: "A bite-sized overview to help you grasp the core ideas quickly",
  },
  {
    id: "critique",
    label: "Critique",
    description: "An expert review of the material, offering constructive analysis and identifying gaps",
  },
  {
    id: "debate",
    label: "Debate",
    description: "A thoughtful debate between two hosts illuminating different perspectives",
  },
];

const LANGUAGES = [
  "English", "Spanish", "French", "German", "Portuguese",
  "Japanese", "Chinese (Simplified)", "Korean", "Arabic", "Italian",
];

export default function NodePodcast({ nodeId, courseId }: NodePodcastProps) {
  const { toast } = useToast();
  const audioRef = useRef<HTMLAudioElement>(null);

  const [podcast, setPodcast] = useState<PodcastRecord | null>(null);
  const [audioUrl, setAudioUrl] = useState<string | null>(null);
  const [loadingExisting, setLoadingExisting] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [showSettings, setShowSettings] = useState(false);

  // Options
  const [format, setFormat] = useState<Format>("deep_dive");
  const [length, setLength] = useState<Length>("default");
  const [language, setLanguage] = useState("English");
  const [focusPrompt, setFocusPrompt] = useState("");
  const [provider, setProvider] = useState<Provider>("openai");

  // Player state
  const [playing, setPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [activeLine, setActiveLine] = useState(-1);

  // Load existing podcast on mount
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoadingExisting(true);
      const { data } = await supabase
        .from("generated_podcasts")
        .select("*")
        .eq("skill_node_id", nodeId)
        .order("created_at", { ascending: false })
        .limit(1)
        .maybeSingle();

      if (cancelled) return;
      if (data) {
        setPodcast(data as PodcastRecord);
        setProvider(data.provider as Provider);
        const { data: { publicUrl } } = supabase.storage
          .from("podcasts")
          .getPublicUrl(data.storage_path);
        setAudioUrl(publicUrl);
      } else {
        setShowSettings(true); // Show settings on first generation
      }
      setLoadingExisting(false);
    })();
    return () => { cancelled = true; };
  }, [nodeId]);

  // Audio event listeners
  useEffect(() => {
    const el = audioRef.current;
    if (!el) return;
    const onTimeUpdate = () => setCurrentTime(el.currentTime);
    const onDurationChange = () => setDuration(el.duration);
    const onEnded = () => { setPlaying(false); setActiveLine(-1); };
    el.addEventListener("timeupdate", onTimeUpdate);
    el.addEventListener("durationchange", onDurationChange);
    el.addEventListener("ended", onEnded);
    return () => {
      el.removeEventListener("timeupdate", onTimeUpdate);
      el.removeEventListener("durationchange", onDurationChange);
      el.removeEventListener("ended", onEnded);
    };
  }, [audioUrl]);

  // Active-line tracking: divide duration evenly across lines
  useEffect(() => {
    if (!podcast || duration <= 0) return;
    const lines = podcast.transcript.length;
    setActiveLine(Math.min(Math.floor((currentTime / duration) * lines), lines - 1));
  }, [currentTime, duration, podcast]);

  const togglePlay = () => {
    const el = audioRef.current;
    if (!el) return;
    if (playing) { el.pause(); setPlaying(false); }
    else { el.play(); setPlaying(true); }
  };

  const restart = () => {
    const el = audioRef.current;
    if (!el) return;
    el.currentTime = 0;
    el.play();
    setPlaying(true);
  };

  const handleSeek = (e: React.ChangeEvent<HTMLInputElement>) => {
    const el = audioRef.current;
    if (!el) return;
    el.currentTime = Number(e.target.value);
  };

  const fmt = (s: number) => {
    const m = Math.floor(s / 60);
    const sec = Math.floor(s % 60);
    return `${m}:${sec.toString().padStart(2, "0")}`;
  };

  const handleGenerate = async () => {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) { toast({ variant: "destructive", title: "Not authenticated" }); return; }

    setGenerating(true);
    setShowSettings(false);
    setPodcast(null);
    setAudioUrl(null);
    setPlaying(false);
    setCurrentTime(0);
    setDuration(0);

    try {
      const { data, error } = await supabase.functions.invoke("generate-node-podcast", {
        body: {
          skillNodeId: nodeId,
          provider,
          format,
          length,
          language,
          focusPrompt: focusPrompt.trim() || undefined,
          _authToken: session.access_token,
        },
      });
      if (error) throw error;
      if (!data?.url) throw new Error("No audio URL returned");

      const { data: row } = await supabase
        .from("generated_podcasts")
        .select("*")
        .eq("id", data.id)
        .single();

      setPodcast(row as PodcastRecord);
      setAudioUrl(data.url);
      toast({ title: "Podcast generated!" });
    } catch (e: any) {
      toast({ variant: "destructive", title: "Generation failed", description: e?.message });
      setShowSettings(true);
    } finally {
      setGenerating(false);
    }
  };

  if (loadingExisting) {
    return (
      <div className="flex items-center gap-2 py-4 text-muted-foreground text-sm">
        <Loader2 className="h-4 w-4 animate-spin" />Loading…
      </div>
    );
  }

  return (
    <div className="space-y-5">

      {/* ── Settings panel ──────────────────────────────────────────────── */}
      {showSettings && (
        <div className="space-y-5">

          {/* Format cards */}
          <div className="space-y-2">
            <Label className="text-sm font-semibold">Format</Label>
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
              {FORMATS.map((f) => (
                <button
                  key={f.id}
                  type="button"
                  onClick={() => setFormat(f.id)}
                  className={`relative text-left rounded-lg border p-3 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary ${
                    format === f.id
                      ? "border-primary bg-primary/5"
                      : "border-border bg-muted/30 hover:bg-muted/60"
                  }`}
                >
                  <div className="flex items-start justify-between gap-1 mb-1.5">
                    <span className="font-semibold text-sm leading-snug">{f.label}</span>
                    {format === f.id && (
                      <Check className="h-3.5 w-3.5 shrink-0 mt-0.5 text-primary" />
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground leading-snug">{f.description}</p>
                </button>
              ))}
            </div>
          </div>

          {/* Language + Length row */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label className="text-sm font-semibold">Language</Label>
              <Select value={language} onValueChange={setLanguage}>
                <SelectTrigger className="h-9">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {LANGUAGES.map((l) => (
                    <SelectItem key={l} value={l}>{l}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1.5">
              <Label className="text-sm font-semibold">Length</Label>
              <div className="flex items-center rounded-md border overflow-hidden h-9 text-sm">
                {(["short", "default", "long"] as Length[]).map((l) => (
                  <button
                    key={l}
                    type="button"
                    onClick={() => setLength(l)}
                    className={`flex-1 flex items-center justify-center gap-1 transition-colors capitalize ${
                      length === l
                        ? "bg-primary text-primary-foreground font-medium"
                        : "bg-background text-muted-foreground hover:bg-muted"
                    }`}
                  >
                    {length === l && <Check className="h-3 w-3 shrink-0" />}
                    {l.charAt(0).toUpperCase() + l.slice(1)}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Focus prompt */}
          <div className="space-y-1.5">
            <Label className="text-sm font-semibold">
              What should the AI hosts focus on in this episode?
              <span className="ml-1 font-normal text-muted-foreground">(optional)</span>
            </Label>
            <Textarea
              value={focusPrompt}
              onChange={(e) => setFocusPrompt(e.target.value)}
              placeholder="e.g. Focus on practical applications and real-world examples…"
              rows={3}
              className="resize-none"
            />
          </div>

          {/* Provider + Generate */}
          <div className="flex flex-wrap items-center gap-3 pt-1">
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">Voice provider</Label>
              <div className="flex items-center rounded-md border overflow-hidden text-xs font-medium h-8">
                {(["openai", "elevenlabs"] as Provider[]).map((p) => (
                  <button
                    key={p}
                    type="button"
                    onClick={() => setProvider(p)}
                    className={`px-3 h-full transition-colors ${
                      provider === p
                        ? "bg-primary text-primary-foreground"
                        : "bg-background text-muted-foreground hover:bg-muted"
                    }`}
                  >
                    {p === "openai" ? "OpenAI TTS" : "ElevenLabs"}
                  </button>
                ))}
              </div>
            </div>

            <Button
              onClick={handleGenerate}
              disabled={generating}
              className="gap-1.5 ml-auto"
            >
              <Headphones className="h-4 w-4" />
              Generate
            </Button>

            {podcast && (
              <Button variant="ghost" size="sm" onClick={() => setShowSettings(false)}>
                Cancel
              </Button>
            )}
          </div>
        </div>
      )}

      {/* ── Generating state ─────────────────────────────────────────────── */}
      {generating && (
        <div className="rounded-lg border bg-muted/30 p-8 text-center space-y-3">
          <Loader2 className="h-8 w-8 animate-spin mx-auto text-primary" />
          <div className="space-y-1">
            <p className="text-sm font-medium">Writing script and synthesizing audio…</p>
            <p className="text-xs text-muted-foreground">This takes about 30–60 seconds.</p>
          </div>
        </div>
      )}

      {/* ── Player ───────────────────────────────────────────────────────── */}
      {audioUrl && podcast && !generating && !showSettings && (
        <div className="space-y-3">
          {/* Metadata row */}
          <div className="flex items-center gap-2 flex-wrap">
            <Badge variant="secondary" className="text-xs capitalize">
              {FORMATS.find(f => f.id === format)?.label ?? "Deep Dive"}
            </Badge>
            <Badge variant="outline" className="text-xs capitalize">{length}</Badge>
            {language !== "English" && (
              <Badge variant="outline" className="text-xs">{language}</Badge>
            )}
            <Badge variant="outline" className="text-xs">
              {podcast.provider === "openai" ? "OpenAI TTS" : "ElevenLabs"}
            </Badge>
            <Button
              variant="ghost"
              size="sm"
              className="ml-auto h-7 gap-1.5 text-xs"
              onClick={() => setShowSettings(true)}
            >
              <Settings2 className="h-3 w-3" />
              Customize & Regenerate
            </Button>
          </div>

          <div className="rounded-lg border bg-muted/20 overflow-hidden">
            <audio ref={audioRef} src={audioUrl} preload="metadata" />

            {/* Player bar */}
            <div className="p-4 border-b">
              <div className="flex items-center gap-3">
                <Button size="icon" variant="default" className="h-9 w-9 shrink-0 rounded-full" onClick={togglePlay}>
                  {playing ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4 ml-0.5" />}
                </Button>
                <Button size="icon" variant="ghost" className="h-8 w-8 shrink-0" onClick={restart} title="Restart">
                  <SkipBack className="h-3.5 w-3.5" />
                </Button>
                <div className="flex-1 flex items-center gap-2 min-w-0">
                  <input
                    type="range"
                    min={0}
                    max={duration || 0}
                    step={0.1}
                    value={currentTime}
                    onChange={handleSeek}
                    className="flex-1 h-1.5 accent-primary cursor-pointer"
                  />
                  <span className="text-xs text-muted-foreground tabular-nums whitespace-nowrap">
                    {fmt(currentTime)} / {fmt(duration)}
                  </span>
                </div>
              </div>
            </div>

            {/* Transcript */}
            <div className="divide-y max-h-80 overflow-y-auto">
              {podcast.transcript.map((line, i) => (
                <div
                  key={i}
                  className={`flex gap-3 px-4 py-3 transition-colors text-sm ${
                    activeLine === i ? "bg-primary/5" : ""
                  }`}
                >
                  <span className={`shrink-0 text-[10px] font-bold uppercase tracking-wider w-14 pt-0.5 ${
                    line.host === "alex"
                      ? "text-blue-600 dark:text-blue-400"
                      : "text-violet-600 dark:text-violet-400"
                  }`}>
                    {line.host === "alex" ? "Alex" : "Jordan"}
                  </span>
                  <p className={`flex-1 leading-relaxed ${
                    activeLine === i ? "text-foreground font-medium" : "text-muted-foreground"
                  }`}>
                    {line.text}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ── Empty state ──────────────────────────────────────────────────── */}
      {!podcast && !generating && !showSettings && (
        <p className="text-sm text-muted-foreground">
          Generate an AI podcast discussion of this lesson — choose a format, length, and focus above.
        </p>
      )}
    </div>
  );
}
